import { AnimatePresence, motion } from 'framer-motion';
import confetti from 'canvas-confetti';
import { Heart, MailOpen } from 'lucide-react';
import { useMemo, useState } from 'react';
import { SectionHeading } from '../components/SectionHeading';
import { Sticker } from '../components/Sticker';
import { usePrivateContent } from '../components/PrivateContentProvider';
import { birthdayConfig } from '../config';
import { useBirthdayAudio } from '../hooks/useBirthdayAudio';

const MAIL_EMOJI = String.fromCodePoint(0x1f48c);

export function LetterSection() {
  const [mainOpen, setMainOpen] = useState(false);
  const [openMini, setOpenMini] = useState<string | null>(null);
  const { playEffect } = useBirthdayAudio();
  const { content, ensureUnlocked } = usePrivateContent();

  const miniLetters = useMemo(
    () =>
      birthdayConfig.copy.letter.miniLetters.map((letter) => ({
        ...letter,
        message:
          content?.[
            letter.messageKey as keyof NonNullable<typeof content>
          ] ?? '',
      })),
    [content],
  );

  const openMainLetter = async () => {
    const allowed = await ensureUnlocked();

    if (!allowed) return;

    if (!mainOpen) {
      playEffect(birthdayConfig.audio.envelopeTrack, false);
      confetti({
        particleCount: 55,
        spread: 65,
        origin: { y: 0.6 },
        colors: ['#ff4fa3', '#ffd166', '#a855f7'],
      });
    }

    setMainOpen(true);
  };

  const toggleMiniLetter = async (letterId: string) => {
    const allowed = await ensureUnlocked();

    if (!allowed) return;

    playEffect(birthdayConfig.audio.envelopeTrack, false);
    setOpenMini((current) => (current === letterId ? null : letterId));
  };

  const formattedLetter = (content?.mainLetter ?? '').replace(
    '[Sender Name]',
    birthdayConfig.sender.name,
  );

  return (
    <section id="letter" className="section-shell letter-section">
      <div className="page-container">
        <SectionHeading
          eyebrow={birthdayConfig.copy.letter.eyebrow}
          title={`For ${birthdayConfig.birthdayPerson.nickname}`}
          description={birthdayConfig.copy.letter.description}
        />

        <div className={`envelope-stage ${mainOpen ? 'opened' : ''}`}>
          <Sticker
            src="/stickers/mochi-shy.svg"
            alt="Shy cute cat beside the letter"
            className="letter-sticker"
          />

          <motion.div
            className="envelope-wrap"
            animate={mainOpen ? { y: 50 } : { y: 0 }}
          >
            <div className="envelope-back" />
            <motion.div
              className="envelope-flap"
              animate={{ rotateX: mainOpen ? 180 : 0 }}
              transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
            />
            <div className="envelope-front">
              <span>
                For {birthdayConfig.birthdayPerson.nickname}
              </span>
            </div>
            <motion.div
              className="wax-seal"
              animate={
                mainOpen
                  ? { scale: 0, rotate: 80, opacity: 0 }
                  : { scale: 1, rotate: 0, opacity: 1 }
              }
            >
              <Heart fill="currentColor" size={26} />
            </motion.div>
          </motion.div>

          <AnimatePresence>
            {mainOpen && formattedLetter && (
              <motion.article
                className="letter-paper"
                initial={{ opacity: 0, y: 110, scale: 0.82 }}
                animate={{ opacity: 1, y: -40, scale: 1 }}
                transition={{
                  delay: 0.38,
                  type: 'spring',
                  stiffness: 110,
                  damping: 17,
                }}
              >
                <span className="letter-corner">{MAIL_EMOJI}</span>
                <p>{formattedLetter}</p>
              </motion.article>
            )}
          </AnimatePresence>

          {!mainOpen && (
            <button
              type="button"
              className="primary-button envelope-open-button"
              onClick={() => void openMainLetter()}
            >
              <MailOpen size={19} />
              {birthdayConfig.copy.letter.openButton}
            </button>
          )}
        </div>

        <div className="mini-envelope-grid">
          {miniLetters.map((letter, index) => {
            const isOpen = openMini === letter.id;

            return (
              <motion.button
                key={letter.id}
                type="button"
                className={`mini-envelope ${isOpen ? 'open' : ''}`}
                onClick={() => void toggleMiniLetter(letter.id)}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="mini-envelope-icon">{MAIL_EMOJI}</div>
                <strong>{letter.title}</strong>

                <AnimatePresence initial={false}>
                  {isOpen && letter.message && (
                    <motion.p
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                    >
                      {letter.message}
                    </motion.p>
                  )}
                </AnimatePresence>

                <span>{isOpen ? 'Close' : 'Tap to open'}</span>
              </motion.button>
            );
          })}
        </div>
      </div>
    </section>
  );
}